export type TRunColumn =
  | 'status'
  | 'id'
  | 'flow'
  | 'by'
  | 'type'
  | 'destination'
  | 'client'
  | 'auth'
  | 'copies'
  | 'agentSteps'
  | 'agentCost'
  | 'took'
  | 'started';

export const RUN_COLUMNS: readonly { key: TRunColumn; label: string }[] = [
  { key: 'status', label: 'Status' },
  { key: 'id', label: 'Run id' },
  { key: 'flow', label: 'Flow' },
  { key: 'by', label: 'Started by' },
  { key: 'type', label: 'Event type' },
  { key: 'destination', label: 'Destinations' },
  { key: 'client', label: 'Customer' },
  { key: 'auth', label: 'Sender check' },
  { key: 'copies', label: 'Copies' },
  { key: 'agentSteps', label: 'Agent steps' },
  { key: 'agentCost', label: 'Cost' },
  { key: 'took', label: 'Took' },
  { key: 'started', label: 'Started' },
];

const KNOWN = new Set<string>(RUN_COLUMNS.map((column) => column.key));

const DEFAULT_RUN_COLUMNS: readonly TRunColumn[] = [
  'status',
  'id',
  'flow',
  'by',
  'copies',
  'agentSteps',
  'agentCost',
  'took',
  'started',
];

const ordered = (columns: readonly string[]): TRunColumn[] => {
  const picked = new Set(columns);
  return RUN_COLUMNS.filter((column) => picked.has(column.key)).map((column) => column.key);
};

const DEFAULT_KEY = ordered(DEFAULT_RUN_COLUMNS).join(',');

/** What the table shows: columns of every run, and paths into each run's trigger body. */
export interface IRunColumns {
  columns: TRunColumn[];
  paths: string[];
}

// The API reads at most five paths per page; each one is a body read.
export const MAX_RUN_PATHS = 5;

const list = (raw: unknown): string[] =>
  Array.isArray(raw) ? raw.map(String) : typeof raw === 'string' && raw ? raw.split(',') : [];

export const parseRunColumns = (search: Record<string, unknown>): IRunColumns => {
  const columns = ordered(list(search.cols).filter((key) => KNOWN.has(key)));
  return {
    columns: columns.length > 0 ? columns : ordered(DEFAULT_RUN_COLUMNS),
    paths: [...new Set(list(search.paths).map((path) => path.trim()))]
      .filter(Boolean)
      .slice(0, MAX_RUN_PATHS),
  };
};

export const runColumnsToSearch = (picked: IRunColumns): Record<string, string> => {
  const key = ordered(picked.columns).join(',');
  return {
    ...(key === DEFAULT_KEY || key.length === 0 ? {} : { cols: key }),
    ...(picked.paths.length > 0 ? { paths: picked.paths.join(',') } : {}),
  };
};

const STORE_KEY = 'hp.run-columns';

export const rememberRunColumns = (picked: IRunColumns): void => {
  localStorage.setItem(STORE_KEY, JSON.stringify(runColumnsToSearch(picked)));
};

// Only consulted when the URL names neither; a link's own columns still win.
export const rememberedRunColumns = (): IRunColumns | null => {
  const stored = localStorage.getItem(STORE_KEY);
  if (!stored) return null;
  try {
    const parsed: unknown = JSON.parse(stored);
    return parsed && typeof parsed === 'object'
      ? parseRunColumns(parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};
